import { noteIntent } from "@/analytics/intent";
import { trackObjectActionInvoked } from "@/utils/analytics";
import type { ObjectActionContext, ObjectKind } from "./types";

export interface ActionInvocationInfo {
  objectKind?: string;
  objectSubtype?: string;
  surface?: string;
  scope?: string;
  presentation?: string;
  selectionCount?: number;
  format?: string;
}

export function objectSubtypeOf(kind: ObjectKind): string | undefined {
  switch (kind.type) {
    case "workspace":
      return kind.scope;
    case "file-record":
      return kind.isFolder ? `${kind.class}:folder` : kind.class;
    case "canvas-node":
      return kind.nodeType;
    case "multi-selection":
      return [...kind.kinds].sort().join("+");
    default:
      return undefined;
  }
}

/** One `Object Action Invoked` + the intent note outcome events consume. */
export function trackActionInvoked(
  action: string,
  info: ActionInvocationInfo = {},
): void {
  trackObjectActionInvoked({ action, ...info });
  noteIntent({
    action,
    surface: info.surface ?? "menu",
    objectKind: info.objectKind,
  });
}

export function withActionTracking<A extends unknown[], R>(
  action: string,
  context: ObjectActionContext,
  handler: (...args: A) => R,
  extra?: Pick<ActionInvocationInfo, "presentation" | "format">,
): (...args: A) => R {
  return (...args: A): R => {
    trackActionInvoked(action, {
      objectKind: context.kind.type,
      objectSubtype: objectSubtypeOf(context.kind),
      surface: context.surface,
      scope: context.scope,
      selectionCount: context.state.selectionCount,
      ...extra,
    });
    return handler(...args);
  };
}
